// Axios client with token auto-attach. Endpoint URLs are the single source of truth.
import axios from 'axios';

const BASE = (import.meta.env && import.meta.env.BASE_URL) || '/';
const API_BASE = (BASE.endsWith('/') ? BASE : BASE + '/') + 'api';

const client = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

client.interceptors.request.use((cfg) => {
  const token = localStorage.getItem('auth_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

client.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      localStorage.removeItem('auth_token');
    }
    return Promise.reject(err);
  }
);

// Every error surfaced in the console goes through this, so a failed action always
// shows the API's own message ("Cannot go from confirmed to completed…") rather
// than a bare "Request failed with status code 409".
export function errText(e) {
  return e?.response?.data?.error || e?.message || 'Something went wrong.';
}

export const endpoints = {
  auth: {
    signup:               '/auth/signup',
    login:                '/auth/login',
    logout:               '/auth/logout',
    me:                   '/auth/me',
    requestPasswordReset: '/auth/request-password-reset',
    resetPassword:        '/auth/reset-password',
    changePassword:       '/auth/me/change-password',
    verifyEmail: (t)  =>  `/auth/verify-email/${t}`,
    resendVerification:   '/auth/me/resend-verification',
    verifyUsername:       '/auth/verify-username',
    deleteSelf:           '/auth/me',
  },
  users: {
    list:                 '/users',
    setRole:    (id) =>   `/users/${id}/role`,
    suspend:    (id) =>   `/users/${id}/suspend`,
    remove:     (id) =>   `/users/${id}`,
    capabilities:(id)=>   `/users/${id}/capabilities`,
  },
  invites: {
    list:     '/invites',
    create:   '/invites',
    bulk:     '/invites/bulk',
    byToken:  (t) => `/invites/by-token/${t}`,
    accept:   '/invites/accept',
    decline:  (t) => `/invites/decline/${t}`,
    resend:   (id) => `/invites/${id}/resend`,
    revoke:   (id) => `/invites/${id}`,
  },
  roles: {
    list:           '/roles',
    create:         '/roles',
    update: (id) => `/roles/${id}`,
    remove: (id) => `/roles/${id}`,
    ensureSystem:   '/roles/ensure-system',
    capabilities:   '/roles/capabilities',
  },
  accessTokens: {
    list:           '/access-tokens',
    create:         '/access-tokens',
    revoke: (id) => `/access-tokens/${id}`,
    rotate: (id) => `/access-tokens/${id}/rotate`,
  },

  // ---- Squeakky operations ----
  config: '/config',
  bookings: {
    board:        '/bookings/board',
    list:         '/bookings',
    get:   (id) => `/bookings/${id}`,
    update:(id) => `/bookings/${id}`,
    status:(id) => `/bookings/${id}/status`,
    assign:(id) => `/bookings/${id}/assign`,
    checklist:(id) => `/bookings/${id}/checklist`,
    photos:(id) => `/bookings/${id}/photos`,
    photo: (id, pid) => `/bookings/${id}/photos/${pid}`,
    walkthrough:(id) => `/bookings/${id}/walkthrough`,
    payment:(id) => `/bookings/${id}/payment`,
    followUp:(id) => `/bookings/${id}/follow-up`,
    cancel:(id) => `/bookings/${id}`,
  },
  quotes: {
    list:          '/quotes',
    get:    (id) => `/quotes/${id}`,
    update: (id) => `/quotes/${id}`,
    consult:(id) => `/quotes/${id}/consult`,
    scope:  (id) => `/quotes/${id}/scope`,
    send:   (id) => `/quotes/${id}/send`,
    decision:(id)=> `/quotes/${id}/decision`,
  },
  services: {
    all:            '/services/all',
    public:         '/services',
    create:         '/services',
    update: (id) => `/services/${id}`,
    checklist:(id)=> `/services/${id}/checklist`,
    remove: (id) => `/services/${id}`,
  },
  areas: {
    all:            '/areas/all',
    public:         '/areas',
    create:         '/areas',
    update: (id) => `/areas/${id}`,
    remove: (id) => `/areas/${id}`,
  },
  teams: {
    list:            '/teams',
    get:     (id) => `/teams/${id}`,
    create:          '/teams',
    update:  (id) => `/teams/${id}`,
    members: (id) => `/teams/${id}/members`,
    member:  (id, mid) => `/teams/${id}/members/${mid}`,
    remove:  (id) => `/teams/${id}`,
  },
  reviews: {
    all:            '/reviews/all',
    public:         '/reviews',
    moderate:(id)=> `/reviews/${id}`,
    reply:  (id) => `/reviews/${id}/reply`,
  },
  waitlist: {
    list:            '/waitlist',
    notified:(id) => `/waitlist/${id}/notified`,
  },
  leads:   '/leads',
  analytics: { summary: '/analytics/summary', activity: '/analytics/activity' },
  admin: { setup: '/admin/setup', seed: '/admin/seed', system: '/admin/system' },
  questions: {
    list:       '/questions',
    get:        (slug) => `/questions/${slug}`,
    create:     '/questions',
    answer:     (slug) => `/questions/${slug}/answers`,
    vote:       (slug) => `/questions/${slug}/vote`,
    voteAnswer: (slug, aid) => `/questions/${slug}/answers/${aid}/vote`,
    accept:     (slug) => `/questions/${slug}/accept`,
  },
  articles: {
    list:    '/articles',
    get:     (slug) => `/articles/${slug}`,
    create:  '/articles',
    like:    (slug) => `/articles/${slug}/like`,
    comment: (slug) => `/articles/${slug}/comments`,
  },
};

export default client;
